import _ from 'lodash';
import dynamic from 'next/dynamic';
import { ReactNode } from 'react';
import { useMediaQuery } from 'react-responsive';
import css from './Viewport.module.scss';

const MediaQuery = dynamic(() => import('react-responsive'), { ssr: false });

/**
 * These viewports and queries here mirror the values in `styles/breakpoints.scss`.
 * Any updates to that file should be reflected here (and vice versa).
 */

const BREAK_MOBILE = _.toNumber(css.breakMobile);
const BREAK_MEDIUM = _.toNumber(css.breakMedium);
const BREAK_LARGE = _.toNumber(css.breakLarge);
const BREAK_XL = _.toNumber(css.breakXL);

const BREAK_XS_HEIGHT = _.toNumber(css.breakXSHeight);

interface IViewportProps {
  children: ReactNode;
  /** Number of pixels to offset the breakpoints by. */
  offset?: number;
}

/**
 * Render children only when the viewport is `< 600px` in height.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
export const useIsXSHeightViewport = (offset: number = 0) =>
  useMediaQuery({ maxHeight: BREAK_XS_HEIGHT + offset - 1 });

/**
 * Render children only when the viewport is `< 768px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const Small = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery maxWidth={BREAK_MOBILE + offset - 1}>{children}</MediaQuery>
);

/**
 * Returns `true` if the viewport is `< 768px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsSmallViewport = (offset: number = 0) =>
  useMediaQuery({ maxWidth: BREAK_MOBILE + offset - 1 });

/**
 * Render children only when the viewport is `< 992px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const MediumDown = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery maxWidth={BREAK_MEDIUM + offset - 1}>{children}</MediaQuery>
);

/**
 * Returns `true` if the viewport is `< 992px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsMediumDownViewport = (offset: number = 0) =>
  useMediaQuery({ maxWidth: BREAK_MEDIUM + offset - 1 });

/**
 * Render children only when the viewport is `>= 768px && < 992px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const Medium = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery
    minWidth={BREAK_MOBILE + offset}
    maxWidth={BREAK_MEDIUM + offset - 1}
  >
    {children}
  </MediaQuery>
);

/**
 * Returns `true` if the viewport is `>= 768px && < 992px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsMediumViewport = (offset: number = 0) =>
  useMediaQuery({
    minWidth: BREAK_MOBILE + offset,
    maxWidth: BREAK_MEDIUM + offset - 1,
  });

/**
 * Render children only when the viewport is `>= 768px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const MediumUp = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery minWidth={BREAK_MOBILE + offset}>{children}</MediaQuery>
);

/**
 * Returns `true` if the viewport is `>= 768px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsMediumUpViewport = (offset: number = 0) =>
  useMediaQuery({ minWidth: BREAK_MOBILE + offset });

/**
 * Render children only when the viewport is `< 1280px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const LargeDown = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery maxWidth={BREAK_LARGE + offset - 1}>{children}</MediaQuery>
);

/**
 * Returns `true` when the viewport is `< 1280px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsLargeDownViewport = (offset: number = 0) =>
  useMediaQuery({ maxWidth: BREAK_LARGE + offset - 1 });

/**
 * Render children only when the viewport is `>= 992px && < 1280px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const Large = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery
    minWidth={BREAK_MEDIUM + offset}
    maxWidth={BREAK_LARGE + offset - 1}
  >
    {children}
  </MediaQuery>
);

/**
 * Returns `true` when the viewport is `>= 992px && < 1280px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsLargeViewport = (offset: number = 0) =>
  useMediaQuery({
    minWidth: BREAK_MEDIUM + offset,
    maxWidth: BREAK_LARGE + offset - 1,
  });

/**
 * Render children only when the viewport is `>= 992px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const LargeUp = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery minWidth={BREAK_MEDIUM + offset}>{children}</MediaQuery>
);

/**
 * Returns `true` when the viewport is `>= 992px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsLargeUpViewport = (offset: number = 0) =>
  useMediaQuery({ minWidth: BREAK_MEDIUM + offset });

/**
 * Render children only when the viewport is `< 1440px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const XLDown = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery maxWidth={BREAK_XL + offset - 1}>{children}</MediaQuery>
);

/**
 * Returns `true` when the viewport is `< 1440px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsXLDownViewport = (offset: number = 0) =>
  useMediaQuery({ maxWidth: BREAK_XL + offset - 1 });

/**
 * Render children only when the viewport is `>= 1280px && < 1440px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const XL = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery minWidth={BREAK_LARGE + offset} maxWidth={BREAK_XL + offset - 1}>
    {children}
  </MediaQuery>
);

/**
 * Returns `true` when the viewport is `>= 1280px && < 1440px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsXLViewport = (offset: number = 0) =>
  useMediaQuery({
    minWidth: BREAK_LARGE + offset,
    maxWidth: BREAK_XL + offset - 1,
  });

/**
 * Returns `true` when the viewport is `>= 1440px` in width.
 *
 * @param {number} offset Number of pixels to offset the breakpoint by.
 * @returns {boolean}
 */
export const useIsXLUpViewport = (offset: number = 0) =>
  useMediaQuery({ minWidth: BREAK_XL + offset });

/**
 * Render children only when the viewport is `>= 1440px` in width.
 *
 * @param {IViewportProps} props
 * @returns {JSX.Element}
 */
const XLUp = ({ children, offset = 0 }: IViewportProps) => (
  <MediaQuery minWidth={BREAK_XL + offset}>{children}</MediaQuery>
);

export const Viewport = {
  Small,
  MediumDown,
  Medium,
  MediumUp,
  LargeDown,
  Large,
  LargeUp,
  XLDown,
  XL,
  XLUp,
};
